import cbreLogo from "@assets/images/cbreLogo.svg";
import warehouse_interior_3 from "@assets/images/warehouse_interior_3.jpg";
import useSurveyState from "@hooks/useSurveyState";
import { useGetSurveyMediaFileQuery } from "@services/media";
import { formatDate } from "@shared/utils"; // Assuming you have a utility function for date formatting
import ContactListPreview from "@survey-cover/ContactListPreview";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import { SurveyContact } from "@survey-types/survey.contacts.types";
import { updateCurrentSurvey } from "@survey/surveySlice";
import { useEffect } from "react";

interface CoverSectionPreviewProps {
  mode?: string;
}

const CoverSectionPreview = (props: CoverSectionPreviewProps) => {
  const { mode } = props;
  const { survey, dispatch } = useSurveyState();
  const {
    coverTitle,
    coverSubTitle,
    coverDate,
    pdfPaperSize,
    clientLogo,
    clientName,
    contacts,
    coverBackgroundImage,
    isDisplayClientLogo,
    isDisplayClientName,
  } = survey || {};

  const filteredSurveyContacts: SurveyContact[][] = [];
  const CONTACTS_PER_PAGE = pdfPaperSize === "Landscape" ? 5 : 6;
  const sortedContacts = contacts
    ? [...contacts].sort((a, b) => (a?.order || 0) - (b?.order || 0))
    : [];
  if (survey && sortedContacts && sortedContacts.length > CONTACTS_PER_PAGE) {
    const itemsLength: number = Math.ceil(
      sortedContacts.length / CONTACTS_PER_PAGE,
    );
    for (let i: number = 0; i < itemsLength; i += 1) {
      filteredSurveyContacts.push(
        sortedContacts.slice(
          i * CONTACTS_PER_PAGE,
          (i + 1) * CONTACTS_PER_PAGE,
        ) as SurveyContact[],
      );
    }
  } else {
    filteredSurveyContacts.push(sortedContacts || []);
  }

  const { data: clientLogoResponse, isSuccess: isClientLogoSuccess } =
    useGetSurveyMediaFileQuery(
      { mediaType: "CLIENTLOGO", surveyId: survey?.id },
      { skip: !survey?.id },
    );

  // Update Redux store when client logo data is available
  useEffect(() => {
    if (isClientLogoSuccess && clientLogoResponse?.media?.[0]?.cloudfrontUrl) {
      const clientLogo = clientLogoResponse.media[0].cloudfrontUrl;

      // Update Redux store if clientLogo is different
      if (clientLogo !== survey?.clientLogo) {
        dispatch(updateCurrentSurvey({ clientLogo }));
      }
    }
  }, [isClientLogoSuccess, clientLogoResponse, survey?.clientLogo]);

  return (
    <>
      {filteredSurveyContacts?.length > 0 &&
        filteredSurveyContacts.map(
          (sortedContacts: SurveyContact[], index: number) => (
            <SurveyPDFPage key={index} mode={mode}>
              <div className="w-2/3 bg-primary-dark flex flex-col">
                {index === 0 && (
                  <div className="p-8">
                    <img src={cbreLogo} className="mb-8" />
                    <h2 className="text-white text-[3.625rem] my-4 font-heading leading-14">
                      {coverTitle || "Property Survey"}
                    </h2>
                    <div className="border-l-2 border-accent pl-4">
                      <p className="text-white mb-1 text-base">
                        {coverSubTitle}
                      </p>
                      <div>
                        {isDisplayClientName && clientName && (
                          <>
                            <span className="text-white mr-4 text-sm">
                              {clientName}
                            </span>
                            <span className="text-white mr-4">|</span>
                          </>
                        )}
                        <span className="text-white text-sm">
                          {coverDate
                            ? coverDate
                            : formatDate(new Date(), "MMMM D, YYYY")}
                        </span>
                      </div>
                    </div>
                    {isDisplayClientLogo && clientLogo && (
                      <div className="my-4.5 mx-6">
                        <img
                          src={clientLogo}
                          className={`text-sm w-auto h-auto ${survey?.isClientLogoTransparent ? "outline-0" : "outline-4 outline-white bg-white"} ${survey?.unrestrictedLogoSize ? "max-w-[250px] max-h-[250px]" : "max-w-[125px] max-h-[125px]"}`}
                          alt="Client Logo"
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <img
                    src={
                      coverBackgroundImage?.cloudfrontUrl ||
                      warehouse_interior_3
                    }
                    alt="Cover Background"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-1/3 bg-secondary-light">
                <ContactListPreview contacts={sortedContacts} />
              </div>
            </SurveyPDFPage>
          ),
        )}
    </>
  );
};

export default CoverSectionPreview;
